<template>
  <div class="inspection-report-container">
    <v-container fluid fill-height>
      <!-- Back Button -->
      <v-btn icon class="back-button" @click="confirmBackNavigation">
        <v-icon>mdi-arrow-left</v-icon>
      </v-btn>

      <!-- Header -->
      <v-row class="pa-7" justify="center">
        <h1 class="page-title">CREATE JOB INSPECTION REPORT</h1>
      </v-row>

      <!-- Main Content Card -->
      <v-row class="fill-height pa-0 ma-0" justify="center" align="start">
        <v-col cols="12" md="10" class="pa-0">
          <v-card elevation="0" class="main-card">
            <v-card-text class="pa-8">
              <v-overlay :value="loading">
                <v-progress-circular indeterminate size="64" />
              </v-overlay>

              <v-form ref="form" v-model="valid">
                <!-- JOB INFORMATION Section -->
                <div class="section-header mb-6">
                  <h2 class="section-title">JOB INFORMATION</h2>
                  <div class="section-divider"></div>
                </div>

                <!-- Row 1: Job Number, Customer Name, Contact Person, Customer Reference -->
                <v-row>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="jobNumber"
                      label="Job Number"
                      dense
                      outlined
                      prepend-inner-icon="mdi-numeric"
                      readonly
                      disabled
                      hint="Auto-generated on save"
                      persistent-hint
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-autocomplete
                      v-model="selectedCustomer"
                      :items="customerOptions"
                      item-text="companyName"
                      return-object
                      label="Customer Name"
                      dense
                      outlined
                      prepend-inner-icon="mdi-account-group"
                      :rules="[rules.required]"
                      @change="handleCustomerSelection"
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="contactPerson"
                      label="Contact Person"
                      dense
                      outlined
                      prepend-inner-icon="mdi-account"
                      readonly
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="customerReference"
                      label="Customer Reference"
                      dense
                      outlined
                      prepend-inner-icon="mdi-file-document-outline"
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- Row 2: Seal Type, Serial Number, Shaft Size, Total Lead Time -->
                <v-row>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="sealType"
                      label="Seal Type"
                      dense
                      outlined
                      prepend-inner-icon="mdi-cog"
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="serialNumber"
                      label="Serial Number"
                      dense
                      outlined
                      prepend-inner-icon="mdi-barcode"
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="shaftSize"
                      label="Shaft Size"
                      dense
                      outlined
                      prepend-inner-icon="mdi-ruler"
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="TotalLead"
                      :items="leadTimeOptions"
                      label="Total Lead Time"
                      dense
                      outlined
                      prepend-inner-icon="mdi-clock"
                      class="nexus-input"
                      :rules="[rules.required]"
                    />
                  </v-col>
                </v-row>

                <!-- Row 3: Engineer, Technician, Salesperson, Sample Number -->
                <v-row>
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="engineer"
                      :items="engineerOptions"
                      item-text="name"
                      item-value="name"
                      label="Engineer"
                      dense
                      outlined
                      prepend-inner-icon="mdi-account-hard-hat"
                      clearable
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="technician"
                      :items="technicianOptions"
                      item-text="name"
                      item-value="name"
                      label="Technician"
                      dense
                      outlined
                      prepend-inner-icon="mdi-account-wrench"
                      clearable
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="salesperson"
                      :items="salespersonOptions"
                      item-text="name"
                      item-value="name"
                      label="Salesperson"
                      dense
                      outlined
                      prepend-inner-icon="mdi-account-tie"
                      clearable
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="sampleNumber"
                      label="Sample Number (Optional)"
                      dense
                      outlined
                      prepend-inner-icon="mdi-test-tube"
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- Row 4: Job Status, Priority, Total Cost, and conditional fields -->
                <v-row>
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="jobStatus"
                      :items="jobStatusOptions"
                      label="Job Status"
                      dense
                      outlined
                      prepend-inner-icon="mdi-information"
                      :rules="[rules.required, validateStatusChange]"
                      class="nexus-input"
                    />
                  </v-col>
                  <v-col cols="12" md="3">
                    <v-select
                      v-model="priority"
                      :items="priorityOptions"
                      label="Priority"
                      dense
                      outlined
                      prepend-inner-icon="mdi-alert"
                      :rules="[rules.required]"
                      class="nexus-input"
                    />
                  </v-col>
                  
                  <!-- Total Cost - Always Visible, right after Priority -->
                  <v-col cols="12" md="3">
                    <v-text-field
                      v-model="totalCost"
                      label="Total Cost"
                      dense
                      outlined
                      prepend-inner-icon="mdi-currency-usd"
                      type="number"
                      class="nexus-input"
                      :rules="totalCostRules"
                    />
                  </v-col>
                  
                  <!-- Quote Number - appears when Quoted -->
                  <v-col cols="12" md="3" v-if="showQuoteNumber">
                    <v-text-field
                      v-model="quoteNumber"
                      label="Quote Number"
                      dense
                      outlined
                      prepend-inner-icon="mdi-file-document-edit"
                      class="nexus-input"
                    />
                  </v-col>
                  
                  <!-- SO Number - appears from In Progress onwards, hidden when Completed -->
                  <v-col cols="12" md="3" v-if="showSONumber">
                    <v-text-field
                      v-model="soNumber"
                      label="SO Number"
                      dense
                      outlined
                      prepend-inner-icon="mdi-file-document"
                      class="nexus-input"
                    />
                  </v-col>
                  
                  <!-- Invoice Number - appears only when Completed -->
                  <v-col cols="12" md="3" v-if="showInvoiceNumber">
                    <v-text-field
                      v-model="invoiceNumber"
                      label="Invoice Number"
                      dense
                      outlined
                      prepend-inner-icon="mdi-receipt"
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- Hidden fields for inspection date and date quoted -->
                <input type="hidden" v-model="inspectionDate" />
                <input type="hidden" v-model="dateQuoted" />

                <!-- Job Description -->
                <v-row>
                  <v-col cols="12">
                    <v-textarea
                      v-model="jobDescription"
                      label="Job Description"
                      outlined
                      dense
                      prepend-inner-icon="mdi-text"
                      rows="3"
                      auto-grow
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- INSPECTION CHECKLIST Section -->
                <div class="section-header mb-6 mt-8">
                  <h2 class="section-title">INSPECTION CHECKLIST</h2>
                  <div class="section-divider"></div>
                </div>

                <!-- Checklist Sections -->
                <v-row v-if="checklist.length">
                  <v-col cols="12">
                    <v-card
                      v-for="(section, sectionIndex) in checklist"
                      :key="sectionIndex"
                      class="section-card mb-6"
                    >
                      <!-- Section Header -->
                      <div class="section-header-bar">
                        <h3 class="section-title-text">
                          <v-icon color="white" class="mr-2">mdi-clipboard-check-outline</v-icon>
                          {{ section.sectionTitle }}
                        </h3>
                      </div>

                      <v-card-text class="pa-6">
                        <v-row dense>
                          <!-- Checklist Items -->
                          <v-col
                            cols="12"
                            v-for="(item, index) in section.items"
                            :key="index"
                            class="checklist-item-row"
                          >
                            <v-card
                              class="item-card"
                              :class="{ 'item-card-error': item.checked === null }"
                            >
                              <v-card-text class="pa-4">
                                <v-row dense align="center">
                                  <!-- Label Column -->
                                  <v-col cols="12" md="6">
                                    <span class="item-label">{{ item.label }}</span>
                                  </v-col>

                                  <!-- Radio Group Column -->
                                  <v-col cols="12" md="6">
                                    <v-radio-group
                                      v-model="item.checked"
                                      row
                                      dense
                                      class="radio-group-custom"
                                      hide-details
                                      @change="handleCheckboxChange(item)"
                                    >
                                      <v-radio label="Yes" value="Yes" color="#475569"></v-radio>
                                      <v-radio label="No" value="No" color="#475569"></v-radio>
                                    </v-radio-group>
                                  </v-col>
                                </v-row>

                                <!-- Conditional Text Fields -->
                                <v-row dense v-if="item.checked === 'Yes'" class="mt-2">
                                  <v-col cols="12">
                                    <v-text-field
                                      v-if="item.showseal1"
                                      v-model="item.seal1"
                                      label="Any visible damages"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showJobNumberField"
                                      v-model="item.jobNumberField"
                                      label="Job Number"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showseal2"
                                      v-model="item.seal2"
                                      label="Special fitting on seal"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showseal3"
                                      v-model="item.seal3"
                                      label="Note Previous Job Number"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showseal4"
                                      v-model="item.seal4"
                                      label="Add photo of anything unusual"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showsettingclipfield"
                                      v-model="item.settingclipfield"
                                      label="Setting Clip"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showoringMaterial"
                                      v-model="item.oringMaterial"
                                      label="O-Ring Material"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showoringdamage"
                                      v-model="item.oringdamage"
                                      label="O-Ring Damages"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showoringSize"
                                      v-model="item.oringSize"
                                      label="Size"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showFace1"
                                      v-model="item.Face1"
                                      label="Replace inboard stationary face"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showFace2"
                                      v-model="item.Face2"
                                      label="Replace inboard Rotary face"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showFace3"
                                      v-model="item.Face3"
                                      label="Result of vacuum test"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showFace4"
                                      v-model="item.Face4"
                                      label="Other Damage to faces"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showOutboardFace1"
                                      v-model="item.OutboardFace1"
                                      label="Replace outboard stationary face"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showOutboardFace2"
                                      v-model="item.OutboardFace2"
                                      label="Replace outboard Rotary Face"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showGland1"
                                      v-model="item.Gland1"
                                      label="O-ring surface NOT acceptable"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showGland2"
                                      v-model="item.Gland2"
                                      label="Damage around setting clip area"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showGland3"
                                      v-model="item.Gland3"
                                      label="Damage on gasket/o-ring surface"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showGland4"
                                      v-model="item.Gland4"
                                      label="Pitting seen on gland"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showGland5"
                                      v-model="item.Gland5"
                                      label="Gland Port Size"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showGland6"
                                      v-model="item.Gland6"
                                      label="Spring holes damaged"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showGland7"
                                      v-model="item.Gland7"
                                      label="Dents/external damage"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showGland8"
                                      v-model="item.Gland8"
                                      label="Pins Bent loose, worn, or missing"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showGland9"
                                      v-model="item.Gland9"
                                      label="Other"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showadd1"
                                      v-model="item.add1"
                                      label="Lip Seals"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showadd2"
                                      v-model="item.add2"
                                      label="Tool Required 1"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showadd3"
                                      v-model="item.add3"
                                      label="Tool Required 2"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showadd4"
                                      v-model="item.add4"
                                      label="Tool Required 3"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showadd5"
                                      v-model="item.add5"
                                      label="Additional Part 1"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showadd6"
                                      v-model="item.add6"
                                      label="Additional Part 2"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showadd7"
                                      v-model="item.add7"
                                      label="Metal Bellows"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showcheck1"
                                      v-model="item.check1"
                                      label="Ovality on sealing surface"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showcheck2"
                                      v-model="item.check2"
                                      label="Damage on Other Parts"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showcheck3"
                                      v-model="item.check3"
                                      label="Additional parts"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showcheck4"
                                      v-model="item.check4"
                                      label="Machining to be Done"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>

                                    <v-text-field
                                      v-if="item.showcheck6"
                                      v-model="item.check6"
                                      label="Other parts and components"
                                      dense
                                      outlined
                                      class="nexus-input mt-2"
                                      :rules="[v => !!v || 'This field is required']"
                                    ></v-text-field>
                                  </v-col>
                                </v-row>
                              </v-card-text>
                            </v-card>
                          </v-col>

                          <!-- Upload and View Photo Buttons -->
                          <v-col cols="12" class="d-flex justify-start mt-4">
                            <v-btn class="nexus-btn mr-3" @click="openUploadOptionsDialog(sectionIndex)">
                              <v-icon left>mdi-camera</v-icon>Upload Photo
                            </v-btn>

                            <v-btn
                              v-if="section.photos.length"
                              color="success"
                              class="nexus-btn-secondary"
                              @click="openPhotoDialog(section.photos, section.sectionTitle)"
                            >
                              <v-icon left>mdi-eye</v-icon>View Photos ({{ section.photos.length }})
                            </v-btn>
                          </v-col>

                          <!-- Upload Loader -->
                          <v-col cols="12" v-if="uploadingPhoto">
                            <v-progress-linear indeterminate color="#475569" class="mb-2"></v-progress-linear>
                            <span class="uploading-text">Uploading photos...</span>
                          </v-col>

                          <!-- Upload Photos Input -->
                          <input
                            :ref="'fileInput' + sectionIndex"
                            type="file"
                            accept="image/*"
                            multiple
                            @change="handleFileUpload($event, sectionIndex)"
                            style="display: none"
                          />
                        </v-row>
                      </v-card-text>
                    </v-card>
                  </v-col>
                </v-row>

                <!-- Comment and Summary Section -->
                <v-row>
                  <v-col cols="12">
                    <v-textarea
                      v-model="comment"
                      label="Comment (For Internal Workshop Only)"
                      outlined
                      dense
                      prepend-inner-icon="mdi-comment-text"
                      rows="3"
                      auto-grow
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <v-row>
                  <v-col cols="12">
                    <v-textarea
                      v-model="jobSummary"
                      label="Summary (Short Description to Send to Customer)"
                      outlined
                      dense
                      prepend-inner-icon="mdi-text-box"
                      rows="3"
                      auto-grow
                      class="nexus-input"
                    />
                  </v-col>
                </v-row>

                <!-- Action Buttons -->
                <v-row justify="center" class="my-6">
                  <v-btn large color="primary" class="nexus-btn" @click="confirmSubmit" :disabled="saving">
                    <v-icon left>mdi-content-save</v-icon>
                    {{ saving ? 'Creating Report...' : 'Create Inspection Report' }}
                  </v-btn>
                </v-row>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Photo Dialog -->
    <v-dialog v-model="photoDialog" max-width="800px">
      <v-card class="nexus-dialog">
        <v-toolbar dark flat class="photo-dialog-header">
          <v-toolbar-title>{{ currentPhotoLabel }}</v-toolbar-title>
          <v-spacer></v-spacer>
          <v-btn icon @click="closePhotoDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>
        <v-carousel v-model="carouselIndex" height="600">
          <v-carousel-item v-for="(photo, index) in selectedPhotos" :key="index" :src="photo"></v-carousel-item>
        </v-carousel>
      </v-card>
    </v-dialog>

    <!-- Upload Options Dialog -->
    <v-dialog v-model="uploadOptionsDialog" max-width="500px">
      <v-card class="nexus-dialog">
        <v-card-title class="nexus-dialog-title">
          <v-icon left color="#475569">mdi-camera</v-icon>
          Select Photo Upload Method
        </v-card-title>
        <v-divider></v-divider>
        <v-card-text class="pa-6">
          <v-row>
            <v-col cols="12">
              <v-btn class="upload-option-btn" @click="takePhoto" block large>
                <v-icon left>mdi-camera</v-icon>
                Use Camera
              </v-btn>
            </v-col>
            <v-col cols="12">
              <v-btn class="upload-option-btn" @click="uploadFromGallery" block large>
                <v-icon left>mdi-image</v-icon>
                Upload from Gallery
              </v-btn>
            </v-col>
            <v-col cols="12">
              <v-btn class="upload-option-btn" @click="uploadFromFiles" block large>
                <v-icon left>mdi-folder</v-icon>
                Upload from Files
              </v-btn>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn text @click="closeUploadOptionsDialog">Cancel</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Confirmation Dialog -->
    <v-dialog v-model="confirmationDialog.visible" max-width="400">
      <v-card class="nexus-dialog">
        <v-card-title class="nexus-dialog-title">{{ confirmationDialog.title }}</v-card-title>
        <v-card-text>{{ confirmationDialog.message }}</v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="success" text @click="confirmationDialog.confirm">Yes</v-btn>
          <v-btn color="grey" text @click="confirmationDialog.visible = false">No</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Snackbar -->
    <v-snackbar v-model="snackbar" color="success" timeout="3000">
      Inspection report created successfully!
      <v-btn color="white" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>

    <!-- Error Snackbar -->
    <v-snackbar v-model="errorSnackbar" color="error" timeout="3000">
      {{ errorMessage }}
      <v-btn color="white" text @click="errorSnackbar = false">Close</v-btn>
    </v-snackbar>

    <!-- Validation Error Snackbar -->
    <v-snackbar v-model="validationErrorSnackbar" color="error" timeout="3000">
      Please answer all checklist items before saving.
      <v-btn color="white" text @click="validationErrorSnackbar = false">Close</v-btn>
    </v-snackbar>

    <!-- Total Cost Required Snackbar -->
    <v-snackbar v-model="totalCostErrorSnackbar" color="error" timeout="3000">
      Total Cost is required to change status to "Costing Complete".
      <v-btn color="white" text @click="totalCostErrorSnackbar = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import {
  getFirestore,
  collection,
  addDoc,
  getDocs,
  query,
  where,
  doc,
  getDoc,
  setDoc,
} from 'firebase/firestore';
import { getStorage, ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { firebaseApp, auth } from '@/firebaseConfig';

const db = getFirestore(firebaseApp);
const storage = getStorage(firebaseApp);

export default {
  name: 'CreateInspectionReport',
  data() {
    return {
      loading: false,
      valid: false,
      saving: false,

      // Job Information Fields
      jobNumber: 'Will be auto-generated',
      selectedCustomer: null,
      customerOptions: [],
      contactPerson: '',
      customerReference: '',
      
      // Conditional Document Fields
      soNumber: '',
      quoteNumber: '',
      invoiceNumber: '',
      
      // Sample Number
      sampleNumber: '',
      
      // Total Cost - Always Visible
      totalCost: '',
      
      // Seal Details
      sealType: '',
      serialNumber: '',
      shaftSize: '',

      // Personnel Dropdowns
      engineer: '',
      engineerOptions: [],
      technician: '',
      technicianOptions: [],
      salesperson: '',
      salespersonOptions: [],

      // Status and Dates
      jobStatus: '',
      jobStatusOptions: ['Inspection', 'Costing Complete','Quoted', 'In Progress', 'Ready', 'Completed', 'Delayed', 'Cancelled'],
      priority: '',
      priorityOptions: ['Low', 'Medium', 'High'],
      inspectionDate: new Date().toISOString().substr(0, 10),
      dateQuoted: '',
      previousJobStatus: '',

      // Description and Comments
      jobDescription: '',
      comment: '',
      jobSummary: '',

      // Total Lead Time
      TotalLead: '',
      leadTimeOptions: [],

      // UI State
      inspectionDateMenu: false,

      // Checklist
      checklist: [],
      uploadingPhoto: false,
      photoDialog: false,
      uploadOptionsDialog: false,
      selectedPhotos: [],
      currentPhotoLabel: '',
      photoLabels: [],
      carouselIndex: 0,
      selectedSectionIndex: null,

      // Dialogs
      confirmationDialog: { visible: false, title: '', message: '', confirm: null },
      snackbar: false,
      errorSnackbar: false,
      errorMessage: '',
      validationErrorSnackbar: false,
      totalCostErrorSnackbar: false,

      // Validation Rules
      rules: {
        required: (v) => !!v || 'Required.',
      },

      // Default Checklist Template
      defaultChecklist: [
        {
          sectionTitle: 'NOTE',
          items: [
            { label: 'Seal Failure Report Requested?', checked: null },
            { label: 'Any visible damage?', checked: null, upload: true, showseal1: false, seal1: '' },
            { label: 'Special Fittings on seal?', checked: null, upload: true, showseal2: false, seal2: '' },
            { label: 'Note Previous Job Number', checked: null, showJobNumberField: false, jobNumberField: '' },
            { label: 'Add Photo of seal as received', checked: null, upload: true },
            { label: 'Add Photo of anything unusual', checked: null, upload: true, showseal3: false, seal3: '' },
          ],
          photos: [],
        },
        {
          sectionTitle: 'SETTING CLIPS',
          items: [
            { label: 'Setting Clips present', checked: null, upload: true },
            { label: 'Setting Clips reusable', checked: null, showsettingclipfield: false, settingclipfield: '' },
          ],
          photos: [],
        },
        {
          sectionTitle: 'O-RINGS',
          items: [
            { label: 'O-ring Material', checked: null, showoringMaterial: false, oringMaterial: '' },
            { label: 'O-ring damages', checked: null, upload: true, showoringdamage: false, oringdamage: '' },
            { label: 'O-ring Sizes', checked: null, upload: true, showoringSize: false, oringSize: '' },
          ],
          photos: [],
        },
        {
          sectionTitle: 'INBOARD FACES',
          items: [
            { label: 'Replace inboard stationary face', checked: null, upload: true, showFace1: false, Face1: '' },
            { label: 'Replace inboard Rotary face', checked: null, upload: true, showFace2: false, Face2: '' },
            { label: 'Result of Vacuum test (if required)', checked: null, showFace3: false, Face3: '' },
            { label: 'Other Damage on faces', checked: null, upload: true, showFace4: false, Face4: '' },
          ],
          photos: [],
        },
        {
          sectionTitle: 'OUTBOARD FACES (if applicable)',
          items: [
            { label: 'Replace outboard stationary face', checked: null, upload: true, showOutboardFace1: false, OutboardFace1: '' },
            { label: 'Replace outboard Rotary face', checked: null, upload: true, showOutboardFace2: false, OutboardFace2: '' },
            { label: 'Result of Vacuum test (if required)', checked: null, showOutboardFace3: false, OutboardFace3: '' },
            { label: 'Other Damage on faces', checked: null, upload: true, showOutboardFace4: false, OutboardFace4: '' },
          ],
          photos: [],
        },
        {
          sectionTitle: 'GLAND',
          items: [
            { label: 'O-ring surfaces acceptable', checked: null, upload: true, showGland1: false, Gland1: '' },
            { label: 'Damage around setting clip area', checked: null, upload: true, showGland2: false, Gland2: '' },
            { label: 'Damage on gasket/o-ring surface', checked: null, upload: true, showGland3: false, Gland3: '' },
            { label: 'Pitting seen on gland', checked: null, upload: true, showGland4: false, Gland4: '' },
            { label: 'Gland port sizes', checked: null, upload: true, showGland5: false, Gland5: '' },
            { label: 'Spring holes damaged', checked: null, upload: true, showGland6: false, Gland6: '' },
            { label: 'Dents/external damage', checked: null, upload: true, showGland7: false, Gland7: '' },
            { label: 'Pins: Bent, Loose, worn, missing', checked: null, upload: true, showGland8: false, Gland8: '' },
            { label: 'Other', checked: null, upload: true, showGland9: false, Gland9: '' },
          ],
          photos: [],
        },
        {
          sectionTitle: 'ADDITIONAL ITEMS',
          items: [
            { label: 'Lip seals', checked: null, upload: true, showadd1: false, add1: '' },
            { label: 'Tool required 1', checked: null, upload: true, showadd2: false, add2: '' },
            { label: 'Tool required 2', checked: null, upload: true, showadd3: false, add3: '' },
            { label: 'Tool required 3', checked: null, upload: true, showadd4: false, add4: '' },
            { label: 'Additional part 1', checked: null, upload: true, showadd5: false, add5: '' },
            { label: 'Additional part 2', checked: null, upload: true, showadd6: false, add6: '' },
            { label: 'Metal bellows', checked: null, upload: true, showadd7: false, add7: '' },
            { label: 'Other', checked: null, upload: true, showadd8: false, add8: '' },
          ],
          photos: [],
        },
        {
          sectionTitle: 'CHECKS',
          items: [
            { label: 'Ovality on sealing surfaces', checked: null, upload: true, showcheck1: false, check1: '' },
            { label: 'Damage on other parts', checked: null, upload: true, showcheck2: false, check2: '' },
            { label: 'Additional parts (Shaft sleeve, Backplate, etc.)', checked: null, upload: true, showcheck3: false, check3: '' },
            { label: 'Note of machining to be done', checked: null, upload: true, showcheck4: false, check4: '' },
            { label: 'Punch gland with new Job Number (if applicable)', checked: null, upload: true, showcheck5: false, check5: '' },
            { label: 'Other parts or components', checked: null, upload: true, showcheck6: false, check6: '' },
          ],
          photos: [],
        },
      ],

      loggedInUser: '',
      loggedInUserName: '',
    };
  },

  computed: {
    isFormValid() {
      return this.checklist.every((section) =>
        section.items.every((item) => item.checked === 'Yes' || item.checked === 'No')
      );
    },
    
    showSONumber() {
      return ['In Progress', 'Ready', 'Delayed'].includes(this.jobStatus);
    },
    
    showQuoteNumber() {
      return this.jobStatus === 'Quoted';
    },
    
    showInvoiceNumber() {
      return this.jobStatus === 'Completed';
    },

    totalCostRules() {
      if (this.jobStatus === 'Costing Complete') {
        return [(v) => !!v || 'Total Cost is required for Costing Complete status'];
      }
      return [];
    },

    validateStatusChange() {
      return (value) => {
        if (value === 'Costing Complete' && !this.totalCost) {
          return 'Total Cost must be filled before changing to Costing Complete';
        }
        return true;
      };
    },
  },

  watch: {
    jobStatus(newStatus, oldStatus) {
      if (newStatus === 'Quoted' && oldStatus !== 'Quoted' && !this.dateQuoted) {
        this.dateQuoted = new Date().toISOString().substr(0, 10);
      }
      
      if (newStatus === 'Costing Complete' && !this.totalCost) {
        this.totalCostErrorSnackbar = true;
        this.$nextTick(() => {
          this.jobStatus = oldStatus;
        });
      }
    },
  },

  async created() {
    try {
      const user = auth.currentUser;
      if (user) {
        this.loggedInUser = user.email;
        await this.fetchUserName(user.email);
      }
    } catch (e) {
      // Handle error silently
    }

    await Promise.all([
      this.fetchCustomerOptions(),
      this.fetchDropdownOptions(),
    ]);
    this.initializeChecklist();
  },

  methods: {
    async fetchUserName(email) {
      try {
        const q = query(collection(db, 'users'), where('email', '==', email));
        const snap = await getDocs(q);
        if (!snap.empty) {
          this.loggedInUserName = snap.docs[0].data().fullName || email;
        } else {
          this.loggedInUserName = email;
        }
      } catch (e) {
        this.loggedInUserName = email;
      }
    },

    async fetchCustomerOptions() {
      try {
        this.loading = true;
        const snapshot = await getDocs(collection(db, 'customers'));
        this.customerOptions = snapshot.docs.map((doc) => ({
          docId: doc.id,
          companyName: doc.data()['Company Name'] || doc.data().customerName || '',
          contactPerson: doc.data()['Contact Person'] || '',
          customerAccount: doc.data()['Customer Account'] || '',
        })).filter(c => c.companyName);
      } catch (error) {
        this.showError('Failed to fetch customers.');
      } finally {
        this.loading = false;
      }
    },

    async fetchDropdownOptions() {
      try {
        const dropdownDoc = await getDoc(doc(db, 'admin', 'dropdownManager'));
        
        if (dropdownDoc.exists()) {
          const data = dropdownDoc.data();
          this.engineerOptions = data.engineers || [];
          this.technicianOptions = data.technicians || [];
          this.salespersonOptions = data.salespersons || [];
          this.leadTimeOptions = data.leadTimes || [];
        } else {
          this.engineerOptions = [];
          this.technicianOptions = [];
          this.salespersonOptions = [];
          this.leadTimeOptions = [];
          this.showError('Dropdown manager configuration not found. Please contact administrator.');
        }
      } catch (error) {
        this.showError('Failed to fetch dropdown options. Please contact administrator.');
        this.engineerOptions = [];
        this.technicianOptions = [];
        this.salespersonOptions = [];
        this.leadTimeOptions = [];
      }
    },

    handleCustomerSelection() {
      if (this.selectedCustomer) {
        this.contactPerson = this.selectedCustomer.contactPerson || '';
      }
    },

    initializeChecklist() {
      this.checklist = JSON.parse(JSON.stringify(this.defaultChecklist));
    },

    handleCheckboxChange(item) {
      if (item.checked === 'No') {
        this.clearDependentFields(item);
      } else if (item.checked === 'Yes') {
        this.toggleFieldVisibility(item);
      }
    },

    clearDependentFields(item) {
      for (const key in item) {
        if (key.startsWith('show')) {
          this.$set(item, key, false);
        }
        if (
          key.match(
            /^(seal|Gland|add|check|Face|OutboardFace|oring|settingclipfield|jobNumberField)$/
          )
        ) {
          if (typeof item[key] === 'string') {
            this.$set(item, key, '');
          } else if (typeof item[key] === 'boolean') {
            this.$set(item, key, false);
          }
        }
      }
    },

    toggleFieldVisibility(item) {
      const label = item.label;
      const visibilityMap = {
        'Any visible damage?': 'showseal1',
        'Special Fittings on seal?': 'showseal2',
        'Note Previous Job Number': 'showJobNumberField',
        'Add Photo of anything unusual': 'showseal3',
        'Setting Clips reusable': 'showsettingclipfield',
        'O-ring Material': 'showoringMaterial',
        'O-ring damages': 'showoringdamage',
        'O-ring Sizes': 'showoringSize',
        'Replace inboard stationary face': 'showFace1',
        'Replace inboard Rotary face': 'showFace2',
        'Result of Vacuum test (if required)': ['showFace3', 'showOutboardFace3'],
        'Other Damage on faces': ['showFace4', 'showOutboardFace4'],
        'Replace outboard stationary face': 'showOutboardFace1',
        'Replace outboard Rotary face': 'showOutboardFace2',
        'O-ring surfaces acceptable': 'showGland1',
        'Damage around setting clip area': 'showGland2',
        'Damage on gasket/o-ring surface': 'showGland3',
        'Pitting seen on gland': 'showGland4',
        'Gland port sizes': 'showGland5',
        'Spring holes damaged': 'showGland6',
        'Dents/external damage': 'showGland7',
        'Pins: Bent, Loose, worn, missing': 'showGland8',
        Other: 'showGland9',
        'Lip seals': 'showadd1',
        'Tool required 1': 'showadd2',
        'Tool required 2': 'showadd3',
        'Tool required 3': 'showadd4',
        'Additional part 1': 'showadd5',
        'Additional part 2': 'showadd6',
        'Metal bellows': 'showadd7',
        'Ovality on sealing surfaces': 'showcheck1',
        'Damage on other parts': 'showcheck2',
        'Additional parts (Shaft sleeve, Backplate, etc.)': 'showcheck3',
        'Note of machining to be done': 'showcheck4',
        'Punch gland with new Job Number (if applicable)': 'showcheck5',
        'Other parts or components': 'showcheck6',
      };

      if (visibilityMap[label]) {
        const fields = Array.isArray(visibilityMap[label])
          ? visibilityMap[label]
          : [visibilityMap[label]];
        fields.forEach((field) => {
          this.$set(item, field, item.checked === 'Yes');
        });
      }
    },

    openUploadOptionsDialog(sectionIndex) {
      this.selectedSectionIndex = sectionIndex;
      this.uploadOptionsDialog = true;
    },

    closeUploadOptionsDialog() {
      this.uploadOptionsDialog = false;
    },

    async takePhoto() {
      this.closeUploadOptionsDialog();
      const fileInput = this.$refs[`fileInput${this.selectedSectionIndex}`][0];
      fileInput.setAttribute('capture', 'camera');
      fileInput.click();
    },

    uploadFromGallery() {
      this.closeUploadOptionsDialog();
      const fileInput = this.$refs[`fileInput${this.selectedSectionIndex}`][0];
      fileInput.removeAttribute('capture');
      fileInput.setAttribute('accept', 'image/*');
      fileInput.click();
    },

    uploadFromFiles() {
      this.closeUploadOptionsDialog();
      const fileInput = this.$refs[`fileInput${this.selectedSectionIndex}`][0];
      fileInput.removeAttribute('capture');
      fileInput.setAttribute('accept', '*/*');
      fileInput.click();
    },

    async handleFileUpload(event, sectionIndex) {
      const files = event.target.files;
      const photos = [];

      this.uploadingPhoto = true;

      try {
        for (const file of files) {
          const uniqueFileName = `${Date.now()}_${file.name}`;
          const storageReference = storageRef(
            storage,
            `inspectionReports/photos/${uniqueFileName}`
          );
          const snapshot = await uploadBytes(storageReference, file);
          const downloadURL = await getDownloadURL(snapshot.ref);
          photos.push(downloadURL);
        }
        this.checklist[sectionIndex].photos.push(...photos);
      } catch (error) {
        this.showError('Failed to upload photos.');
      } finally {
        this.uploadingPhoto = false;
      }
    },

    openPhotoDialog(photos, label) {
      this.selectedPhotos = photos;
      this.photoLabels = new Array(photos.length).fill(label);
      this.currentPhotoLabel = label;
      this.carouselIndex = 0;
      this.photoDialog = true;
    },

    closePhotoDialog() {
      this.photoDialog = false;
    },

    async generateJobNumber() {
      try {
        const counterRef = doc(db, 'admin', 'counters');
        const counterDoc = await getDoc(counterRef);
        
        let nextJobNumber = 8445;
        
        if (counterDoc.exists()) {
          const data = counterDoc.data();
          nextJobNumber = data.lastJobReportNumber >= 8445 ? data.lastJobReportNumber + 1 : 8445;
        }
        
        await setDoc(counterRef, { lastJobReportNumber: nextJobNumber }, { merge: true });
        
        return nextJobNumber.toString();
      } catch (error) {
        this.showError('Failed to generate job number.');
        throw error;
      }
    },

    confirmSubmit() {
      if (!this.$refs.form.validate()) {
        this.showError('Please fill in all required fields correctly.');
        return;
      }

      if (!this.isFormValid) {
        this.validationErrorSnackbar = true;
        return;
      }

      if (this.jobStatus === 'Costing Complete' && !this.totalCost) {
        this.totalCostErrorSnackbar = true;
        return;
      }

      this.confirmationDialog = {
        visible: true,
        title: 'Confirm Create',
        message: 'Create this inspection report?',
        confirm: this.submitForm,
      };
    },

    async submitForm() {
      if (!this.isFormValid || this.saving) return;

      this.saving = true;
      this.loading = true;

      try {
        const generatedJobNumber = await this.generateJobNumber();

        const reportData = {
          jobNumber: generatedJobNumber,
          customerName: this.selectedCustomer ? this.selectedCustomer.companyName : '',
          customerId: this.selectedCustomer ? this.selectedCustomer.docId : '',
          contactPerson: this.contactPerson,
          customerReference: this.customerReference,
          soNumber: this.soNumber || null,
          quoteNumber: this.quoteNumber || null,
          invoiceNumber: this.invoiceNumber || null,
          sampleNumber: this.sampleNumber || null,
          totalCost: this.totalCost || null,
          sealType: this.sealType,
          serialNumber: this.serialNumber,
          shaftSize: this.shaftSize,
          engineer: this.engineer,
          technician: this.technician,
          salesperson: this.salesperson,
          jobStatus: this.jobStatus,
          priority: this.priority,
          inspectionDate: this.inspectionDate,
          dateQuoted: this.dateQuoted || null,
          jobDescription: this.jobDescription,
          comment: this.comment,
          jobSummary: this.jobSummary,
          TotalLead: this.TotalLead,
          checklist: this.checklist,
          reportNotes: '',
          createdBy: this.loggedInUserName,
          createdDate: new Date().toISOString(),
          updatedDate: new Date().toISOString(),
          updatedBy: this.loggedInUserName,
        };

        await addDoc(collection(db, 'inspectionReports'), reportData);

        this.snackbar = true;

        this.$refs.form.reset();
        this.initializeChecklist();
        this.jobNumber = 'Will be auto-generated';
        this.selectedCustomer = null;
        this.contactPerson = '';
        this.customerReference = '';
        this.soNumber = '';
        this.quoteNumber = '';
        this.invoiceNumber = '';
        this.sampleNumber = '';
        this.totalCost = '';
        this.sealType = '';
        this.serialNumber = '';
        this.shaftSize = '';
        this.engineer = '';
        this.technician = '';
        this.salesperson = '';
        this.jobStatus = '';
        this.dateQuoted = '';
        this.comment = '';
        this.jobSummary = '';
        this.TotalLead = '';

        setTimeout(() => {
          this.$router.go(-1);
        }, 1500);
      } catch (e) {
        this.showError('Failed to create inspection report.');
      } finally {
        this.loading = false;
        this.saving = false;
        this.confirmationDialog.visible = false;
      }
    },

    confirmBackNavigation() {
      this.confirmationDialog = {
        visible: true,
        title: 'Confirm Navigation',
        message: 'Go back and discard changes?',
        confirm: this.goBack,
      };
    },

    goBack() {
      this.confirmationDialog.visible = false;
      this.$router.go(-1);
    },

    showError(msg) {
      this.errorMessage = msg;
      this.errorSnackbar = true;
    },

    getChecklistHeader(sectionTitle) {
      return sectionTitle;
    },
  },
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700&display=swap');

.inspection-report-container {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  min-height: 100vh;
  font-family: 'Inter', sans-serif;
}

.page-title {
  color: white;
  font-weight: 700;
  font-size: 2.5rem;
  text-align: center;
  letter-spacing: 2px;
  margin: 0;
}

.main-card {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 20px 40px -12px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1);
  margin-top: 20px;
  transition: all 0.3s ease;
}

.section-header {
  display: flex;
  align-items: center;
  margin-bottom: 24px;
}

.section-title {
  color: #475569;
  font-weight: 600;
  font-size: 1.25rem;
  letter-spacing: 1px;
  margin: 0;
  white-space: nowrap;
}

.section-divider {
  flex: 1;
  height: 2px;
  background: linear-gradient(90deg, #475569 0%, rgba(71, 85, 105, 0.2) 100%);
  margin-left: 20px;
  border-radius: 1px;
}

.nexus-input >>> .v-input__control {
  border-radius: 12px;
}

.nexus-input >>> .v-input__slot {
  background: rgba(248, 250, 252, 0.8) !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  transition: all 0.3s ease;
}

.nexus-input >>> .v-input__slot:hover {
  border-color: #475569 !important;
  background: rgba(255, 255, 255, 0.9) !important;
}

.nexus-input >>> .v-input--is-focused .v-input__slot {
  border-color: #475569 !important;
  background: rgba(255, 255, 255, 1) !important;
  box-shadow: 0 0 0 3px rgba(71, 85, 105, 0.15);
}

.section-card {
  background: rgba(255, 255, 255, 0.98);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 20px 40px -12px rgba(0, 0, 0, 0.3), 0 0 0 1px rgba(255, 255, 255, 0.1);
  overflow: hidden;
  transition: all 0.3s ease;
}

.section-header-bar {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  padding: 16px 24px;
  display: flex;
  align-items: center;
}

.section-title-text {
  color: white;
  font-weight: 600;
  font-size: 1.25rem;
  letter-spacing: 1px;
  display: flex;
  align-items: center;
  margin: 0;
}

.checklist-item-row {
  margin-bottom: 12px;
}

.item-card {
  background: rgba(248, 250, 252, 0.8);
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  transition: all 0.3s ease;
}

.item-card:hover {
  background: rgba(255, 255, 255, 0.9);
  border-color: #475569;
  box-shadow: 0 4px 12px rgba(71, 85, 105, 0.15);
}

.item-card-error {
  border: 2px solid #dc2626 !important;
  background: rgba(254, 226, 226, 0.3) !important;
}

.item-label {
  font-weight: 500;
  color: #1e293b;
  font-size: 0.95rem;
}

.radio-group-custom >>> .v-input__control {
  justify-content: flex-start;
}

.radio-group-custom >>> .v-radio {
  margin-right: 24px;
}

.nexus-btn {
  background: linear-gradient(135deg, #475569 0%, #334155 100%) !important;
  color: white !important;
  border-radius: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: none !important;
  box-shadow: 0 4px 14px 0 rgba(71, 85, 105, 0.3) !important;
  transition: all 0.3s ease !important;
}

.nexus-btn:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 8px 25px 0 rgba(71, 85, 105, 0.4) !important;
}

.nexus-btn-secondary {
  background: rgba(255, 255, 255, 0.95) !important;
  color: #475569 !important;
  border-radius: 12px !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  text-transform: none !important;
  border: 1px solid #e2e8f0 !important;
  box-shadow: 0 2px 8px 0 rgba(71, 85, 105, 0.15) !important;
  transition: all 0.3s ease !important;
}

.nexus-btn-secondary:hover {
  transform: translateY(-2px) !important;
  box-shadow: 0 6px 20px 0 rgba(71, 85, 105, 0.25) !important;
  border-color: #475569 !important;
}

.back-button {
  color: white !important;
  background: rgba(71, 85, 105, 0.9) !important;
  backdrop-filter: blur(10px) !important;
  border-radius: 50% !important;
  position: absolute;
  top: 24px;
  left: 24px;
  transition: all 0.3s ease !important;
  box-shadow: 0 4px 14px 0 rgba(71, 85, 105, 0.4) !important;
}

.back-button:hover {
  transform: scale(1.1) !important;
  box-shadow: 0 8px 25px 0 rgba(71, 85, 105, 0.5) !important;
}

.uploading-text {
  color: #475569;
  font-weight: 500;
  font-size: 0.9rem;
}

.nexus-dialog {
  border-radius: 20px !important;
  background: rgba(255, 255, 255, 0.98) !important;
  backdrop-filter: blur(10px) !important;
}

.nexus-dialog-title {
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%) !important;
  color: #1e293b !important;
  font-weight: 600 !important;
  letter-spacing: 0.5px !important;
  border-bottom: 1px solid #e2e8f0 !important;
  display: flex;
  align-items: center;
}

.photo-dialog-header {
  background: linear-gradient(135deg, #475569 0%, #334155 100%) !important;
}

.upload-option-btn {
  background: rgba(248, 250, 252, 0.9) !important;
  color: #1e293b !important;
  border: 1px solid #e2e8f0 !important;
  border-radius: 12px !important;
  font-weight: 500 !important;
  text-transform: none !important;
  transition: all 0.3s ease !important;
}

.upload-option-btn:hover {
  background: rgba(255, 255, 255, 1) !important;
  border-color: #475569 !important;
  box-shadow: 0 4px 12px rgba(71, 85, 105, 0.2) !important;
}

.v-overlay >>> .v-overlay__content {
  background: rgba(15, 23, 42, 0.8) !important;
  backdrop-filter: blur(4px) !important;
  border-radius: 16px !important;
  padding: 30px !important;
}

::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: linear-gradient(135deg, #475569 0%, #334155 100%);
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: linear-gradient(135deg, #334155 0%, #1e293b 100%);
}

@media (max-width: 768px) {
  .page-title {
    font-size: 1.75rem;
    letter-spacing: 1px;
  }

  .section-title {
    font-size: 1.1rem;
  }

  .section-title-text {
    font-size: 1.1rem;
  }

  .main-card {
    margin: 10px;
    border-radius: 16px;
  }

  .main-card >>> .v-card__text {
    padding: 20px !important;
  }
}
</style>